import { useState, useCallback } from 'react';
import { AvatarPicker } from '../components/AvatarPicker';
import { NicknameForm } from '../components/NicknameForm';
import { updateAvatar, updateNickname, deleteAccount } from '../services/profileService';
import styles from './ProfilePage.module.css';

export interface ProfilePageProps {
  onBack: () => void;
  /** Current avatar from user profile */
  currentAvatar?: string;
  /** Current nickname from user profile */
  currentNickname?: string;
  /** Whether the user is in an active game session */
  isInGame?: boolean;
}

export function ProfilePage({
  onBack,
  currentAvatar = '',
  currentNickname = '',
  isInGame = false,
}: ProfilePageProps) {
  // Avatar state
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState('');
  const [avatarError, setAvatarError] = useState('');

  // Nickname state
  const [nicknameSuccess, setNicknameSuccess] = useState('');
  const [nicknameError, setNicknameError] = useState('');

  // Deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ─── Avatar handlers ────────────────────────────────────────────────────

  const handleAvatarSelect = useCallback((avatar: string) => {
    if (!isInGame) {
      setSelectedAvatar(avatar);
      setAvatarSuccess('');
      setAvatarError('');
    }
  }, [isInGame]);

  const handleAvatarSave = useCallback(async () => {
    if (!selectedAvatar || isInGame) return;

    setAvatarSaving(true);
    setAvatarSuccess('');
    setAvatarError('');

    try {
      await updateAvatar(selectedAvatar);
      setAvatarSuccess('Avatar updated successfully');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to update avatar';
      setAvatarError(message);
    } finally {
      setAvatarSaving(false);
    }
  }, [selectedAvatar, isInGame]);

  // ─── Nickname handler ───────────────────────────────────────────────────

  const handleNicknameComplete = useCallback(async (nickname: string) => {
    setNicknameSuccess('');
    setNicknameError('');

    try {
      await updateNickname(nickname);
      setNicknameSuccess('Nickname updated successfully');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to update nickname';
      setNicknameError(message);
    }
  }, []);

  // ─── Deletion handlers ──────────────────────────────────────────────────

  const handleDeleteRequest = useCallback(() => {
    setShowDeleteConfirm(true);
    setDeleteError('');
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    setDeleteError('');

    try {
      await deleteAccount();
      // On successful deletion, navigate away
      onBack();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to delete account';
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  }, [onBack]);

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className={styles.profilePage}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onBack}
          aria-label="Back to lobby"
        >
          ← Back
        </button>
        <h1 className={styles.title}>Profile</h1>
      </header>

      {/* Avatar Section */}
      <section className={styles.section} aria-label="Change avatar">
        <h2 className={styles.sectionTitle}>Avatar</h2>

        {isInGame && (
          <p className={styles.disabledMessage} role="alert">
            Avatar changes are not permitted during an active game
          </p>
        )}

        <AvatarPicker
          onSelect={handleAvatarSelect}
          selected={selectedAvatar}
        />

        <button
          type="button"
          className={styles.saveButton}
          onClick={handleAvatarSave}
          disabled={avatarSaving || isInGame || !selectedAvatar || selectedAvatar === currentAvatar}
        >
          {avatarSaving ? 'Saving...' : 'Save Avatar'}
        </button>

        {avatarSuccess && (
          <p className={styles.successMessage} role="status">
            {avatarSuccess}
          </p>
        )}
        {avatarError && (
          <p className={styles.errorMessage} role="alert">
            {avatarError}
          </p>
        )}
      </section>

      {/* Nickname Section */}
      <section className={styles.section} aria-label="Change nickname">
        <h2 className={styles.sectionTitle}>Nickname</h2>

        <NicknameForm
          onComplete={handleNicknameComplete}
          initialValue={currentNickname}
        />

        {nicknameSuccess && (
          <p className={styles.successMessage} role="status">
            {nicknameSuccess}
          </p>
        )}
        {nicknameError && (
          <p className={styles.errorMessage} role="alert">
            {nicknameError}
          </p>
        )}
      </section>

      {/* Account Deletion Section */}
      <section className={styles.dangerSection} aria-label="Delete account">
        <h2 className={styles.dangerTitle}>Danger Zone</h2>
        <p className={styles.dangerDescription}>
          Permanently delete your account and all associated data. This action cannot be undone and
          your data will be removed within 30 days.
        </p>

        <button
          type="button"
          className={styles.deleteButton}
          onClick={handleDeleteRequest}
          disabled={deleting}
        >
          Delete Account
        </button>

        {deleteError && (
          <p className={styles.errorMessage} role="alert">
            {deleteError}
          </p>
        )}
      </section>

      {/* Deletion Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className={styles.confirmOverlay} role="dialog" aria-labelledby="confirm-delete-title" aria-modal="true">
          <div className={styles.confirmDialog}>
            <h2 id="confirm-delete-title" className={styles.confirmTitle}>
              Delete your account?
            </h2>
            <p className={styles.confirmText}>
              This will permanently delete your profile, game history, and leaderboard entries.
              Your data will be removed within 30 days. This action cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmCancel}
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmDelete}
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
